import type { RootState } from '@/store';
import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { type ColumnWidthsMap, getColumnWidths, putTableColumnWidths } from './columnWidths.api';

export interface ColumnWidthsState {
  widths: ColumnWidthsMap;
  status: 'idle' | 'loading' | 'loaded';
}

const initialState: ColumnWidthsState = { widths: {}, status: 'idle' };

// Every resizable table calls this on mount; `condition` makes only the first
// dispatch actually fetch — the rest see status !== 'idle' and no-op, so one
// GET serves all of them.
export const fetchColumnWidths = createAsyncThunk<ColumnWidthsMap, void, { state: RootState }>(
  'columnWidths/fetch',
  async () => getColumnWidths(),
  {
    condition: (_, { getState }) => getState().columnWidths.status === 'idle',
  },
);

export const saveTableColumnWidths = createAsyncThunk<
  { tableKey: string; widths: Record<string, number> },
  { tableKey: string; widths: Record<string, number> },
  { state: RootState }
>('columnWidths/saveTable', async ({ tableKey, widths }) => {
  await putTableColumnWidths(tableKey, widths);
  return { tableKey, widths };
});

export const columnWidthsSlice = createSlice({
  name: 'columnWidths',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchColumnWidths.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchColumnWidths.fulfilled, (state, action: PayloadAction<ColumnWidthsMap>) => {
        // A drag that landed while the fetch was in flight wins over the
        // fetched snapshot for the table it touched.
        state.widths = { ...action.payload, ...state.widths };
        state.status = 'loaded';
      })
      .addCase(fetchColumnWidths.rejected, state => {
        state.status = 'loaded';
      })
      .addCase(saveTableColumnWidths.fulfilled, (state, action) => {
        state.widths[action.payload.tableKey] = {
          ...state.widths[action.payload.tableKey],
          ...action.payload.widths,
        };
      });
  },
});

export const selectTableColumnWidths =
  (tableKey: string) =>
  (state: RootState): Record<string, number> =>
    state.columnWidths.widths[tableKey] ?? {};

export default columnWidthsSlice.reducer;
